import { create } from 'zustand'
import type {
  InventoryCategory,
  InventoryStock,
  InventoryTransaction,
} from '@/features/inventory/types/inventory'

type State = {
  inventoryStock: Array<InventoryStock>
  transactions: Array<InventoryTransaction>
  categories: Array<InventoryCategory>

  // Stock management
  setInventoryStock: (items: Array<InventoryStock>) => void
  addInventoryStock: (item: InventoryStock) => void
  updateInventoryStock: (item: InventoryStock) => void
  removeInventoryStock: (stockId: string) => void

  // Transaction management
  setTransactions: (transactions: Array<InventoryTransaction>) => void
  addTransaction: (transaction: InventoryTransaction) => void

  // Category management
  setCategories: (categories: Array<InventoryCategory>) => void
  addCategory: (category: InventoryCategory) => void
  updateCategory: (category: InventoryCategory) => void
  removeCategory: (categoryId: string) => void
}

const initialInventoryStock: Array<InventoryStock> = []
const initialTransactions: Array<InventoryTransaction> = []
const initialCategories: Array<InventoryCategory> = []

export const useInventoryStore = create<State>((set, get) => ({
  inventoryStock: initialInventoryStock,
  transactions: initialTransactions,
  categories: initialCategories,

  // Stock actions
  setInventoryStock: (items) => set({ inventoryStock: items }),
  addInventoryStock: (item) => {
    set({ inventoryStock: [...get().inventoryStock, item] })
  },
  updateInventoryStock: (item) => {
    set({
      inventoryStock: get().inventoryStock.map((x) =>
        x._id === item._id ? { ...x, ...item } : x,
      ),
    })
  },
  removeInventoryStock: (stockId) =>
    set({
      inventoryStock: get().inventoryStock.filter((x) => x._id !== stockId),
    }),

  // Transaction actions
  setTransactions: (transactions) => set({ transactions }),
  addTransaction: (transaction) => {
    set({ transactions: [...get().transactions, transaction] })

    // Update stock quantity based on transaction type
    set({
      inventoryStock: get().inventoryStock.map((stock) => {
        if (
          stock.item === transaction.item &&
          stock.churchId === transaction.churchId
        ) {
          const newQuantity =
            transaction.type === 'IN'
              ? stock.quantity + transaction.quantity
              : Math.max(0, stock.quantity - transaction.quantity)
          return {
            ...stock,
            quantity: newQuantity,
            updatedAt: transaction.createdAt,
          }
        }
        return stock
      }),
    })
  },

  // Category actions
  setCategories: (categories) => set({ categories }),
  addCategory: (category) => {
    set({ categories: [...get().categories, category] })
  },
  updateCategory: (category) => {
    set({
      categories: get().categories.map((c) =>
        c._id === category._id ? { ...c, ...category } : c,
      ),
    })
  },
  removeCategory: (categoryId) =>
    set({
      categories: get().categories.filter((c) => c._id !== categoryId),
    }),
}))
